import React from 'react';
import { Box, HStack, Text, useBreakpointValue, VStack } from '@chakra-ui/react';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import { DrawerBackdrop, DrawerContent, DrawerRoot } from '@/components/ui/drawer';
import { PopoverAnchor, PopoverContent, PopoverRoot } from '@/components/ui/popover';

interface ProposalScheduleEditorProps {
	/** Names what is being changed, since neither container inherits the chip's label. */
	title: string;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	/** The chip that opens it. Rendered whether or not the editor is open. */
	trigger: React.ReactNode;
	children: React.ReactNode;
	/** Offered only where there is a correction to take back. Closes on the way. */
	onUndo?: () => void;
	/**
	 * Give the sheet a Done button, for a field with no finishing move of its own.
	 *
	 * The frequency editor is two selects and never says it is finished, so on a phone there is
	 * nothing to end on but the dimmed strip above the sheet. A calendar is not like that — picking
	 * the day is the answer, and the caller closes on it.
	 */
	showDone?: boolean;
}

/**
 * The container a review card's schedule editors open in — a popover on a desktop, a sheet on a
 * phone.
 *
 * It used to be neither: the fields unfolded inside the card on every width. That pushed the card's
 * own content down and left the editor competing with the verdict buttons underneath it, on the
 * surface with the least room to spare. It also had no way to be dismissed except the Done button,
 * so a stray press left it open with nothing to say so.
 *
 * Both containers close on Escape and on a press outside, which is what a household will try first.
 *
 * The two presentations are chosen rather than drawn together and hidden by CSS: a popover anchors
 * to its trigger and a sheet does not, so rendering both would mount two copies of a field holding
 * one value.
 */
const ProposalScheduleEditor: React.FC<ProposalScheduleEditorProps> = (props: ProposalScheduleEditorProps) => {
	const { title, isOpen, onOpenChange, trigger, children, onUndo, showDone = false } = props;
	// Undefined on the first paint before the query resolves, which would flash the wrong container.
	// A phone is the narrower assumption and the safer one to be wrong about: a sheet on a desktop is
	// merely large, while a popover on a phone can open off the side of the screen.
	const isDesktop = useBreakpointValue({ base: false, md: true }, { ssr: false }) ?? false;

	/**
	 * The title, and any Done button, are the sheet's alone.
	 *
	 * A popover opens anchored under the chip that was just pressed, next to the value it is about —
	 * writing "Change frequency" above a frequency field sat under a frequency chip is the third time
	 * of asking, and a Done beside it repeats what a press anywhere outside already does, with the
	 * whole card in reach to press. The popover keeps the words as its accessible name, which is the
	 * one place they were doing work for everybody.
	 *
	 * The sheet has none of that. It rises over a backdrop with the card behind it, so it has to say
	 * what it is, and the only way out is a strip of dimmed screen above it — which is a thing you
	 * have to already know. A button on the title's row is the one obvious way back.
	 */
	const body = (isSheet: boolean) => (
		<VStack align="stretch" gap={3}>
			{isSheet ? (
				// Done sits on the title's row rather than under the field: at the foot of a sheet it
				// reads as the button that commits the answer, when the field has already taken it and
				// this only puts the sheet away.
				<HStack justify="space-between" align="center" gap={3}>
					<Text textStyle="label" color="fg.muted">
						{title}
					</Text>
					{showDone ? (
						<OutlinedButton size="sm" flexShrink={0} onClick={() => onOpenChange(false)}>
							Done
						</OutlinedButton>
					) : null}
				</HStack>
			) : null}
			{children}
			{onUndo ? (
				<HStack gap={2} justify="flex-end">
					<OutlinedButton
						size="sm"
						onClick={() => {
							onUndo();
							onOpenChange(false);
						}}
					>
						Undo
					</OutlinedButton>
				</HStack>
			) : null}
		</VStack>
	);

	if (isDesktop) {
		return (
			<PopoverRoot
				open={isOpen}
				onOpenChange={(event) => onOpenChange(event.open)}
				positioning={{ placement: 'bottom-start' }}
			>
				{/* An anchor, not a trigger: the chip already owns its own press, and a trigger's click
				    would toggle the same state a second time and cancel it out. This only gives the
				    positioner a box to measure against. */}
				<PopoverAnchor>{trigger}</PopoverAnchor>
				{/* Not portalled, for the same reason as the sheet: sent to the body it lands outside
				    the review dialog's focus trap. It anchors correctly either way now that the chip
				    is wrapped in an anchor the positioner can measure. */}
				<PopoverContent
					portalled={false}
					p={4}
					borderRadius="2xl"
					width="auto"
					minW="280px"
					maxW="360px"
					aria-label={title}
				>
					{body(false)}
				</PopoverContent>
			</PopoverRoot>
		);
	}

	return (
		<>
			{trigger}
			{/* Not portalled. The review card is already inside a dialog, and a sheet sent to the body
			    lands outside that dialog's focus trap — rendered, inert, and unreachable by keyboard.
			    Kept inside, it stays in the trap; it is fixed-position either way, so it still sits
			    against the bottom of the viewport. */}
			<DrawerRoot open={isOpen} onOpenChange={(event) => onOpenChange(event.open)} placement="bottom">
				<DrawerBackdrop />
				<DrawerContent
					portalled={false}
					borderTopRadius="2xl"
					pb="env(safe-area-inset-bottom)"
					aria-label={title}
				>
					<Box p={4}>{body(true)}</Box>
				</DrawerContent>
			</DrawerRoot>
		</>
	);
};

export default ProposalScheduleEditor;
